import {
  ActionIcon,
  Alert,
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconPlus,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { CustomInput } from '../CustomInput';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import { autoCorrectPhrase } from '@commons/autoCorrectPhrase';

const ConfirmAction = ({
  confirmCallback,
  isDisabled,
}: {
  confirmCallback: () => void;
  isDisabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  return (
    <>
      <ActionIcon
        id="infinite-list-delete-trigger-button"
        aria-label={intl.formatMessage({ id: 'delete' })}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
        variant="default"
      >
        <IconTrash size={16} />
      </ActionIcon>

      <Modal
        centered
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        title={`${intl.formatMessage({ id: 'areYouSureYouWantToDelete' })}?`}
        zIndex={1000}
      >
        <Group justify="flex-end" mt="md">
          <Button
            id="infinite-list-delete-cancel-button"
            onClick={() => setIsOpen(false)}
            variant="default"
            disabled={isDisabled}
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button
            id="infinite-list-delete-confirm-button"
            onClick={() => {
              setIsOpen(false);
              confirmCallback();
            }}
            color="red"
            disabled={isDisabled}
          >
            {intl.formatMessage({ id: 'delete' })}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export const InfiniteList = ({
  inputs,
  label,
  onChange,
  value,
  diacritics,
  autoCorrect,
  isDisabled,
  isReadonly,
}: {
  inputs?: {
    type: string;
    name: string;
    values?: { id: string; label: string }[];
    availableImages?: { name: string; link: string }[];
    maxSelection?: number;
  }[];
  label?: string;
  onChange?: (entries: any[]) => void;
  value?: any[];
  diacritics?: string[];
  autoCorrect?: string[];
  isDisabled?: boolean;
  isReadonly?: boolean;
}) => {
  const intl = useIntl();

  const createDefaultEntry = () => {
    const defaultEntry: any = {};
    inputs?.forEach((input) => {
      defaultEntry[input.name] =
        input.type === 'imagePicker' || input.type === 'checkboxGroup'
          ? []
          : input.type === 'datepicker' || input.type === 'signature'
            ? null
            : '';
    });
    return defaultEntry;
  };

  const [entries, setEntries] = useState<any[]>(value || []);
  const [entry, setEntry] = useState<any>(createDefaultEntry());
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAutoCorrect = (item: any, callback: (item: any) => void) => {
    autoCorrect?.forEach((key: string) => {
      item[key] = autoCorrectPhrase(item[key]);
    });

    callback(item);
  };

  const handleDiacritics = (item: any, callback: (item: any) => void) => {
    const requests: any[] = [];
    const diacriticsKeys: string[] = [];

    diacritics?.forEach((key: string) => {
      if (!item[key] || item[key].startsWith(`\\`)) {
        // Skip if value starts with backslash or is empty
        requests.push(
          Promise.resolve({
            json: () => Promise.resolve({ name: item[key].slice(1) || '' }),
          })
        );
      } else {
        setIsLoading(true);

        if (process.env.REACT_APP_SHOULD_ADD_DIACRITICS === 'true') {
          requests.push(
            fetch(`${process.env.REACT_APP_DIACRITICS_API}/lookup`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ name: item[key] }),
            })
          );
        } else {
          requests.push(
            Promise.resolve({
              json: () => Promise.resolve({ name: item[key] }),
            })
          );
        }
      }
      diacriticsKeys.push(key);
    });

    Promise.all(requests)
      .then((responses) => {
        // Wait for all JSON parsing promises to complete
        return Promise.all(responses.map((response) => response.json()));
      })
      .then((dataArray) => {
        // Update all fields at once
        const updatedItem = { ...item };

        dataArray.forEach((data: any, index: number) => {
          const key = diacriticsKeys[index];
          if (key) {
            updatedItem[key] = data.name;
          }
        });

        setIsLoading(false);
        // Call callback once with all updates
        callback(updatedItem);
      })
      .catch((error) => {
        console.error('Error applying diacritics:', error);
        setIsLoading(false);
        // Call callback with original item on error
        callback(item);
      });
  };

  const addCallback = (item: any) => {
    setEntries([...entries, item]);
    setEntry(createDefaultEntry());
    setIsOpen(false);
    setIsLoading(false);
  };

  const updateCallback = (item: any) => {
    // Find the original entry to check if it was new
    const originalEntry = entries.find((i) => i.id === item.id);
    const wasNew = originalEntry?.isNew === true;

    // If it was a new entry, preserve the isNew flag and remove isEditing if present
    // Otherwise, keep isEditing flag for existing entries
    let updatedItem = item;
    if (wasNew) {
      const { isEditing, ...itemWithoutIsEditing } = item;
      updatedItem = { ...itemWithoutIsEditing, isNew: true };
    }

    setEntries(entries.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
    setEntry(createDefaultEntry());
    setIsEditOpen(false);
    setIsOpen(false);
    setIsLoading(false);
  };

  const removeCallback = (item: any) => {
    setEntries(
      entries.map((i) => {
        if (i.id === item.id) {
          // Remove all flags (isNew, isEditing) and only keep isDeleted
          const { isNew, isEditing, ...itemWithoutFlags } = i;
          return { ...itemWithoutFlags, isDeleted: true };
        }
        return i;
      })
    );
    setEntry(createDefaultEntry());
    setIsLoading(false);
  };

  const addItem = (item: any) => {
    // Process diacritics and autoCorrect sequentially to ensure both work correctly
    const processedItem = { ...item };

    // First, process diacritics if needed
    if (diacritics && diacritics.length > 0) {
      handleDiacritics(processedItem, (diacriticsItem: any) => {
        // Then process autoCorrect on the result of diacritics
        if (autoCorrect && autoCorrect.length > 0) {
          handleAutoCorrect(diacriticsItem, (finalItem: any) => {
            addCallback(finalItem);
          });
        } else {
          addCallback(diacriticsItem);
        }
      });
    } else if (autoCorrect && autoCorrect.length > 0) {
      // Only autoCorrect needed
      handleAutoCorrect(processedItem, (finalItem: any) => {
        addCallback(finalItem);
      });
    } else {
      // No processing needed
      addCallback(processedItem);
    }
  };

  const removeItem = (item: any) => {
    removeCallback(item);
  };

  const editItem = (item: any) => {
    setEntry(item);
    setIsEditOpen(true);
    setIsOpen(true);
  };

  const handleUpdateItem = (item: any) => {
    // Process diacritics and autoCorrect sequentially to ensure both work correctly
    const processedItem = { ...item };

    // First, process diacritics if needed
    if (diacritics && diacritics.length > 0) {
      handleDiacritics(processedItem, (diacriticsItem: any) => {
        // Then process autoCorrect on the result of diacritics
        if (autoCorrect && autoCorrect.length > 0) {
          handleAutoCorrect(diacriticsItem, (finalItem: any) => {
            updateCallback(finalItem);
          });
        } else {
          updateCallback(diacriticsItem);
        }
      });
    } else if (autoCorrect && autoCorrect.length > 0) {
      // Only autoCorrect needed
      handleAutoCorrect(processedItem, (finalItem: any) => {
        updateCallback(finalItem);
      });
    } else {
      // No processing needed
      updateCallback(processedItem);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setIsEditOpen(false);
    setEntry(createDefaultEntry());
  };

  useEffect(() => {
    onChange?.(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  return (
    <Stack gap="md">
      <Text fz="sm" fw={500}>
        {label}
      </Text>

      {entries
        .filter((i) => !i.isDeleted)
        .sort((a: any, b: any) => a.name?.localeCompare(b.name) || 0)
        .map((entry: any, index: number) => {
          const getDisplayValue = (input: any, value: any) => {
            if (input.type === 'signature') {
              return (
                <Flex align="center" gap={4}>
                  {![null, ''].includes(value) ? (
                    <IconCheck size={16} color="blue" />
                  ) : (
                    <IconX size={16} color="red" />
                  )}
                </Flex>
              );
            } else if (input.type === 'datepicker') {
              return value
                ? new Date(value).toLocaleDateString('ro-RO', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  })
                : '';
            } else if (input.type === 'imagePicker') {
              return Array.isArray(value) ? value.join(', ') : '';
            } else if (input.type === 'checkboxGroup') {
              return Array.isArray(value)
                ? (input.items || [])
                    .filter((v: any) => value.includes(v.id))
                    .map((v: any) => v.label)
                    .join(', ')
                : '';
            }
            return value;
          };

          return (
            <React.Fragment key={index}>
              <Flex justify="space-between" align="center" gap="xs">
                <Text fz="sm">{index + 1}.</Text>

                <Table
                  variant="vertical"
                  style={{ width: '100%' }}
                  withTableBorder
                  withColumnBorders
                >
                  <Table.Tbody>
                    {inputs?.map((input: any) => {
                      const value = entry[input.name];
                      const displayValue = getDisplayValue(input, value);

                      return (
                        <Table.Tr key={input.name}>
                          <Table.Th style={{ width: '30%' }}>
                            <Text fz="sm">{input.label}</Text>
                          </Table.Th>
                          <Table.Td>
                            {React.isValidElement(displayValue) ? (
                              displayValue
                            ) : (
                              <Text fz="sm">{displayValue}</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>

                <Flex gap={4}>
                  <ActionIcon
                    id="infinite-list-edit-button"
                    aria-label={intl.formatMessage({ id: 'edit' })}
                    onClick={() => editItem(entry)}
                    disabled={isDisabled || isReadonly}
                    variant="default"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>

                  <ConfirmAction
                    confirmCallback={() => removeItem(entry)}
                    isDisabled={isDisabled || isReadonly}
                  />
                </Flex>
              </Flex>
            </React.Fragment>
          );
        })}

      <Button
        fullWidth
        variant="light"
        size="xs"
        id="infinite-list-add-trigger-button"
        onClick={() => setIsOpen(true)}
        disabled={isDisabled || isReadonly}
        leftSection={<IconPlus size={16} />}
      >
        {intl.formatMessage({ id: 'add' })}
      </Button>

      <Modal
        centered
        opened={isOpen}
        onClose={handleCancel}
        title={
          isEditOpen
            ? intl.formatMessage({ id: 'edit' })
            : intl.formatMessage({ id: 'add' })
        }
        zIndex={1000}
      >
        <Stack gap="md">
          {inputs?.length && inputs.length > 0 && (
            <Alert
              variant="light"
              color="blue"
              icon={<IconAlertTriangle />}
              title={intl.formatMessage({ id: 'informativeNote' })}
            >
              <Stack gap="md">
                <Text fz="sm">
                  {intl.formatMessage({
                    id: 'changeDisclaimer',
                  })}
                </Text>
              </Stack>
            </Alert>
          )}

          {inputs?.map((input: any) => (
            <CustomInput
              key={input.name}
              value={entry?.[input.name]}
              name={input.name}
              label={input.label}
              type={input.type}
              items={input.items ? input.items : []}
              availableImages={input.availableImages}
              maxSelection={input.maxSelection}
              dateMinValue={input.dateMinValue}
              description={input.description}
              isDisabled={isDisabled || isReadonly}
              isReadonly={isReadonly}
              onChange={(value) => {
                setEntry({ ...entry, [input.name]: value });
              }}
            />
          ))}
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button
            id="infinite-list-add-cancel-button"
            onClick={handleCancel}
            variant="default"
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          {isEditOpen && (
            <Button
              id="infinite-list-add-update-button"
              disabled={isLoading}
              onClick={() => {
                // Check if the entry being edited was originally new
                const originalEntry = entries.find((i) => i.id === entry.id);
                const wasNew = originalEntry?.isNew === true;

                // If it was new, keep isNew flag; otherwise use isEditing flag
                const flagToAdd = wasNew
                  ? { isNew: true }
                  : { isEditing: true };
                handleUpdateItem({ ...entry, ...flagToAdd });
              }}
              variant="default"
              leftSection={isLoading ? <Loader size="xs" /> : undefined}
            >
              {intl.formatMessage({ id: 'change' })}
            </Button>
          )}
          {!isEditOpen && (
            <Button
              id="infinite-list-add-confirm-button"
              disabled={isLoading}
              onClick={() => {
                addItem({ ...entry, id: uuidv4(), isNew: true });
              }}
              leftSection={isLoading ? <Loader size="xs" /> : undefined}
            >
              {intl.formatMessage({ id: 'add' })}
            </Button>
          )}
        </Group>
      </Modal>
    </Stack>
  );
};
